import type { EntityImportConfig } from '../types';

const API = {
  downloadTemplate: '/method/saas_pos.api.data_import.api.download_template',
  importRows: '/method/saas_pos.api.data_import.api.import_rows',
};

function parseFlag(value: string | undefined, defaultYes = false): string {
  if (value === undefined || value === null || String(value).trim() === '') {
    return defaultYes ? '1' : '0';
  }
  const s = String(value).trim().toLowerCase();
  if (['1', 'yes', 'true', 'y', 'active', 'on'].includes(s)) return '1';
  if (['0', 'no', 'false', 'n', 'inactive', 'off'].includes(s)) return '0';
  return defaultYes ? '1' : '0';
}

export const categoryImportConfig: EntityImportConfig = {
  entityId: 'category',
  title: 'Import Categories',
  description: 'Import product and service categories. Do this first if your categories are new.',
  doctypeLabel: 'POS Item Category',
  closeLabel: 'Back to Products',
  fileHints: [
    'Category Type: Product or Service (default Product)',
    'Duplicate category names with the same type are skipped',
  ],
  fields: [
    { fieldname: 'category_name', label: 'Category Name', required: true, fieldtype: 'Data' },
    {
      fieldname: 'category_type',
      label: 'Category Type',
      required: false,
      fieldtype: 'Data',
      hint: 'Allowed: Product, Service (default: Product)',
    },
  ],
  headerAliases: {
    category_name: ['category name', 'category', 'name'],
    category_type: ['category type', 'type'],
  },
  localStorageKey: 'data-import:category',
  onSuccessInvalidateKeys: [['categories'], ['items']],
  skipReasonLabels: {
    invalid_required: 'Required field missing',
    invalid_type: 'Invalid category type',
    duplicate: 'Duplicate in file',
    already_exists: 'Already exists',
    manual_skip: 'Manually skipped',
  },
  normalizeRow: (raw) => ({
    category_name: (raw.category_name || '').trim(),
    category_type: (raw.category_type || 'Product').trim() || 'Product',
  }),
  clientValidateRow: (row, ctx) => {
    if (!row.category_name?.trim()) {
      return { reason: 'invalid_required', label: 'Category name is required' };
    }
    const t = (row.category_type || 'Product').trim();
    const title = t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
    if (title && !['Product', 'Service'].includes(title) && t) {
      const normalized = t.toLowerCase();
      if (normalized !== 'product' && normalized !== 'service') {
        return { reason: 'invalid_type', label: 'Category Type must be Product or Service' };
      }
    }
    const key = `${row.category_name.trim().toLowerCase()}|${title || 'Product'}`;
    const prev = ctx.seenKeys.get(key);
    if (prev !== undefined) {
      return { reason: 'duplicate', label: `Duplicate category (also on row ${prev})` };
    }
    ctx.seenKeys.set(key, ctx.rowNumber);
    return null;
  },
  api: API,
};

export const productImportConfig: EntityImportConfig = {
  entityId: 'product',
  title: 'Import Products',
  description:
    'Import products and services. Missing categories are created automatically. Set Has Variants = 1 for items that need a Variants file next.',
  doctypeLabel: 'POS Item',
  closeLabel: 'Back to Products',
  showUomGuide: true,
  fileHints: [
    'Type: Product or Service (default Product)',
    'Has Variants / Maintain Stock / Allow Fractional Qty: 1, 0, Yes, or No',
    'For variant items set Has Variants = 1, then import variants using Parent Item Name',
  ],
  fields: [
    { fieldname: 'item_name', label: 'Item Name', required: true, fieldtype: 'Data' },
    { fieldname: 'category', label: 'Category', required: false, fieldtype: 'Data', hint: 'Created automatically if it does not exist' },
    {
      fieldname: 'item_type',
      label: 'Type',
      required: false,
      fieldtype: 'Data',
      hint: 'Allowed: Product, Service (default: Product)',
    },
    {
      fieldname: 'unit_of_measurement',
      label: 'UOM',
      required: false,
      fieldtype: 'Data',
      hint: 'See available UOMs below (default: pcs)',
    },
    { fieldname: 'sale_price', label: 'Sale Price', required: false, fieldtype: 'Data', hint: 'Ignored when Has Variants = 1' },
    { fieldname: 'cost_price', label: 'Cost Price', required: false, fieldtype: 'Data' },
    { fieldname: 'barcode', label: 'Barcode', required: false, fieldtype: 'Data', hint: 'Ignored when Has Variants = 1' },
    {
      fieldname: 'has_variants',
      label: 'Has Variants',
      required: false,
      fieldtype: 'Check',
      hint: 'Allowed: 1 / 0 / Yes / No (default: 0)',
    },
    {
      fieldname: 'maintain_stock',
      label: 'Maintain Stock',
      required: false,
      fieldtype: 'Check',
      hint: 'Allowed: 1 / 0 / Yes / No (default: 0)',
    },
    {
      fieldname: 'allow_fractional_qty',
      label: 'Allow Fractional Qty',
      required: false,
      fieldtype: 'Check',
      hint: 'Allowed: 1 / 0 / Yes / No (default: 0)',
    },
    {
      fieldname: 'opening_balance',
      label: 'Opening Balance',
      required: false,
      fieldtype: 'Data',
      hint: 'Optional. Ignored when Has Variants = 1 or Maintain Stock = 0',
    },
  ],
  headerAliases: {
    item_name: ['item name', 'name', 'product name', 'product'],
    category: ['category', 'category name'],
    item_type: ['type', 'item type'],
    unit_of_measurement: ['uom', 'unit', 'unit of measurement'],
    sale_price: ['sale price', 'price', 'selling price'],
    cost_price: ['cost price', 'cost', 'purchase price'],
    barcode: ['barcode', 'bar code', 'ean'],
    has_variants: ['has variants', 'variants', 'is variant'],
    maintain_stock: ['maintain stock', 'stock', 'track stock'],
    allow_fractional_qty: ['allow fractional qty', 'fractional', 'fraction uom'],
    opening_balance: ['opening balance', 'opening qty', 'opening stock', 'initial stock'],
  },
  localStorageKey: 'data-import:product',
  onSuccessInvalidateKeys: [['items'], ['categories']],
  skipReasonLabels: {
    invalid_required: 'Required field missing',
    duplicate: 'Duplicate in file',
    invalid_type: 'Invalid type',
    invalid_uom: 'Unknown UOM',
    manual_skip: 'Manually skipped',
  },
  normalizeRow: (raw) => {
    const hasVariants = parseFlag(raw.has_variants, false);
    return {
      item_name: (raw.item_name || '').trim(),
      category: (raw.category || '').trim(),
      item_type: (raw.item_type || 'Product').trim() || 'Product',
      unit_of_measurement: (raw.unit_of_measurement || 'pcs').trim() || 'pcs',
      sale_price: hasVariants === '1' ? '0' : (raw.sale_price || '').trim(),
      cost_price: (raw.cost_price || '').trim(),
      barcode: hasVariants === '1' ? '' : (raw.barcode || '').trim(),
      has_variants: hasVariants,
      maintain_stock: parseFlag(raw.maintain_stock, false),
      allow_fractional_qty: parseFlag(raw.allow_fractional_qty, false),
      opening_balance: (raw.opening_balance || '').trim(),
    };
  },
  clientValidateRow: (row, ctx) => {
    if (!row.item_name?.trim()) {
      return { reason: 'invalid_required', label: 'Item name is required' };
    }
    const t = (row.item_type || 'Product').trim().toLowerCase();
    if (t && t !== 'product' && t !== 'service') {
      return { reason: 'invalid_type', label: 'Type must be Product or Service' };
    }
    const nameKey = `name:${row.item_name.trim().toLowerCase()}`;
    const prev = ctx.seenKeys.get(nameKey);
    if (prev !== undefined) {
      return { reason: 'duplicate', label: `Duplicate item name (also on row ${prev})` };
    }
    ctx.seenKeys.set(nameKey, ctx.rowNumber);

    if (row.barcode) {
      const bKey = `bc:${row.barcode.trim().toLowerCase()}`;
      const bPrev = ctx.seenKeys.get(bKey);
      if (bPrev !== undefined) {
        return { reason: 'duplicate', label: `Duplicate barcode (also on row ${bPrev})` };
      }
      ctx.seenKeys.set(bKey, ctx.rowNumber);
    }
    return null;
  },
  api: API,
};

export const productVariantImportConfig: EntityImportConfig = {
  entityId: 'product_variant',
  title: 'Import Variants',
  description:
    'Import variants for products that already exist with Has Variants = 1. Use Parent Item Name (exact name from the products import).',
  doctypeLabel: 'POS Item Variant',
  closeLabel: 'Back to Products',
  showUomGuide: true,
  fileHints: [
    'Parent Item Name must match an existing product with Has Variants = 1',
    'UOM is optional (defaults to the parent item UOM)',
  ],
  fields: [
    {
      fieldname: 'parent_item_name',
      label: 'Parent Item Name',
      required: true,
      fieldtype: 'Data',
      hint: 'Exact item name from your products list / import',
    },
    { fieldname: 'variant_name', label: 'Variant Name', required: true, fieldtype: 'Data' },
    { fieldname: 'price', label: 'Price', required: true, fieldtype: 'Data' },
    { fieldname: 'cost_price', label: 'Cost Price', required: false, fieldtype: 'Data' },
    { fieldname: 'barcode', label: 'Barcode', required: false, fieldtype: 'Data' },
    {
      fieldname: 'unit_of_measurement',
      label: 'UOM',
      required: false,
      fieldtype: 'Data',
      hint: 'See available UOMs below (optional)',
    },
    {
      fieldname: 'opening_balance',
      label: 'Opening Balance',
      required: false,
      fieldtype: 'Data',
      hint: 'Optional. Only when parent item has Maintain Stock enabled',
    },
  ],
  headerAliases: {
    parent_item_name: [
      'parent item name',
      'parent name',
      'parent item',
      'item name',
      'parent',
      'parent item code',
      'item code',
    ],
    variant_name: ['variant name', 'variant', 'name', 'size', 'option'],
    price: ['price', 'sale price', 'selling price'],
    cost_price: ['cost price', 'cost'],
    barcode: ['barcode', 'bar code'],
    unit_of_measurement: ['uom', 'unit', 'unit of measurement'],
    opening_balance: ['opening balance', 'opening qty', 'opening stock', 'initial stock'],
  },
  localStorageKey: 'data-import:product_variant',
  onSuccessInvalidateKeys: [['items']],
  skipReasonLabels: {
    invalid_required: 'Required field missing',
    duplicate: 'Duplicate in file',
    manual_skip: 'Manually skipped',
  },
  normalizeRow: (raw) => ({
    parent_item_name: (raw.parent_item_name || '').trim(),
    variant_name: (raw.variant_name || '').trim(),
    price: (raw.price || '').trim(),
    cost_price: (raw.cost_price || '').trim(),
    barcode: (raw.barcode || '').trim(),
    unit_of_measurement: (raw.unit_of_measurement || '').trim(),
    opening_balance: (raw.opening_balance || '').trim(),
  }),
  clientValidateRow: (row, ctx) => {
    if (!row.parent_item_name?.trim()) {
      return { reason: 'invalid_required', label: 'Parent Item Name is required' };
    }
    if (!row.variant_name?.trim()) {
      return { reason: 'invalid_required', label: 'Variant name is required' };
    }
    if (row.price === '' || row.price === undefined) {
      return { reason: 'invalid_required', label: 'Price is required' };
    }
    const key = `${row.parent_item_name.trim().toLowerCase()}|${row.variant_name.trim().toLowerCase()}`;
    const prev = ctx.seenKeys.get(key);
    if (prev !== undefined) {
      return { reason: 'duplicate', label: `Duplicate variant (also on row ${prev})` };
    }
    ctx.seenKeys.set(key, ctx.rowNumber);

    if (row.barcode) {
      const bKey = `bc:${row.barcode.trim().toLowerCase()}`;
      const bPrev = ctx.seenKeys.get(bKey);
      if (bPrev !== undefined) {
        return { reason: 'duplicate', label: `Duplicate barcode (also on row ${bPrev})` };
      }
      ctx.seenKeys.set(bKey, ctx.rowNumber);
    }
    return null;
  },
  api: API,
};

export const priceUpdateImportConfig: EntityImportConfig = {
  entityId: 'price_update',
  title: 'Update Prices (Excel)',
  description: 'Update existing product or variant prices. Match rows by Barcode (preferred) or Item Code.',
  doctypeLabel: 'POS Item prices',
  closeLabel: 'Back to Products',
  fileHints: [
    'Barcode or Item Code is required on each row',
    'Leave price columns blank to keep current values',
    'For variant items, use the variant barcode',
  ],
  fields: [
    { fieldname: 'barcode', label: 'Barcode', required: false, fieldtype: 'Data' },
    { fieldname: 'item_code', label: 'Item Code', required: false, fieldtype: 'Data' },
    { fieldname: 'item_name', label: 'Item Name', required: false, fieldtype: 'Data', hint: 'Reference only' },
    { fieldname: 'category', label: 'Category', required: false, fieldtype: 'Data', hint: 'Reference only' },
    { fieldname: 'sale_price', label: 'Sale Price', required: false, fieldtype: 'Data' },
    { fieldname: 'wholesale_price', label: 'Wholesale Price', required: false, fieldtype: 'Data' },
    { fieldname: 'minimum_price', label: 'Minimum Price', required: false, fieldtype: 'Data' },
    { fieldname: 'cost_price', label: 'Cost Price', required: false, fieldtype: 'Data' },
  ],
  headerAliases: {
    barcode: ['barcode', 'bar code', 'ean'],
    item_code: ['item code', 'code', 'sku'],
    item_name: ['item name', 'name', 'product name'],
    category: ['category', 'category name'],
    sale_price: ['sale price', 'price', 'retail price', 'new price'],
    wholesale_price: ['wholesale price', 'wholesale'],
    minimum_price: ['minimum price', 'min price', 'floor price'],
    cost_price: ['cost price', 'cost'],
  },
  localStorageKey: 'data-import:price_update',
  onSuccessInvalidateKeys: [['items'], ['bulkPrintItems']],
  skipReasonLabels: {
    invalid_required: 'Barcode or Item Code required',
    manual_skip: 'Manually skipped',
  },
  normalizeRow: (raw) => ({
    barcode: (raw.barcode || '').trim(),
    item_code: (raw.item_code || '').trim(),
    item_name: (raw.item_name || '').trim(),
    category: (raw.category || '').trim(),
    sale_price: (raw.sale_price || '').trim(),
    wholesale_price: (raw.wholesale_price || '').trim(),
    minimum_price: (raw.minimum_price || '').trim(),
    cost_price: (raw.cost_price || '').trim(),
  }),
  clientValidateRow: (row) => {
    if (!row.barcode?.trim() && !row.item_code?.trim()) {
      return { reason: 'invalid_required', label: 'Barcode or Item Code is required' };
    }
    const hasPrice = ['sale_price', 'wholesale_price', 'minimum_price', 'cost_price'].some(
      (k) => row[k as keyof typeof row]?.toString().trim(),
    );
    if (!hasPrice) {
      return { reason: 'invalid_required', label: 'At least one price column is required' };
    }
    return null;
  },
  api: API,
};

export const productUpdateImportConfig: EntityImportConfig = getProductUpdateImportConfig([]);

export function getProductUpdateImportConfig(customFields: { fieldname: string; label: string }[] = []): EntityImportConfig {
  const cfFields = customFields.map((cf) => ({
    fieldname: cf.fieldname,
    label: cf.label || cf.fieldname,
    required: false,
    fieldtype: 'Data' as const,
    hint: 'Optional custom attribute',
  }));

  const cfAliases: Record<string, string[]> = {};
  customFields.forEach((cf) => {
    cfAliases[cf.fieldname] = [cf.label, cf.fieldname, `cf_${cf.fieldname}`].filter(Boolean) as string[];
  });

  return {
  entityId: 'product_update',
  title: 'Bulk Product Update',
  description: 'Update names, attributes, and prices for existing products. Match rows by Item ID / Variant ID from the exported file.',
  doctypeLabel: 'POS Item',
  closeLabel: 'Back to Bulk Update',
  showUomGuide: true,
  fileHints: [
    'Do not change Item ID, Variant ID, Item Code, or Barcode columns',
    'Only filled-in columns are updated; blank cells are ignored',
    'Use the Instructions sheet in the downloaded Excel file',
    'Category must already exist in your store',
  ],
  fields: [
    { fieldname: 'item_id', label: 'Item ID (DO NOT EDIT)', required: false, fieldtype: 'Data', hint: 'System ID — do not change' },
    { fieldname: 'variant_id', label: 'Variant ID (DO NOT EDIT)', required: false, fieldtype: 'Data', hint: 'System ID — do not change' },
    { fieldname: 'item_code', label: 'Item Code (DO NOT EDIT)', required: false, fieldtype: 'Data', hint: 'Do not change' },
    { fieldname: 'barcode', label: 'Barcode (DO NOT EDIT)', required: false, fieldtype: 'Data', hint: 'Do not change' },
    { fieldname: 'item_name', label: 'Item Name', required: false, fieldtype: 'Data' },
    { fieldname: 'variant_name', label: 'Variant Name', required: false, fieldtype: 'Data' },
    { fieldname: 'category', label: 'Category', required: false, fieldtype: 'Data' },
    { fieldname: 'unit_of_measurement', label: 'UOM', required: false, fieldtype: 'Data' },
    { fieldname: 'sale_price', label: 'Sale Price', required: false, fieldtype: 'Data' },
    { fieldname: 'wholesale_price', label: 'Wholesale Price', required: false, fieldtype: 'Data' },
    { fieldname: 'reseller_price', label: 'Reseller Price', required: false, fieldtype: 'Data' },
    { fieldname: 'minimum_price', label: 'Minimum Price', required: false, fieldtype: 'Data' },
    { fieldname: 'cost_price', label: 'Cost Price', required: false, fieldtype: 'Data' },
    { fieldname: 'maintain_stock', label: 'Maintain Stock', required: false, fieldtype: 'Check' },
    { fieldname: 'is_weighted', label: 'Is Weighted', required: false, fieldtype: 'Check' },
    { fieldname: 'plu_code', label: 'PLU Code', required: false, fieldtype: 'Data' },
    { fieldname: 'item_tax_template', label: 'Item Tax Template', required: false, fieldtype: 'Data' },
    ...cfFields,
  ],
  headerAliases: {
    item_id: ['item id (do not edit)', 'item id', 'item_id', 'id'],
    variant_id: ['variant id (do not edit)', 'variant id', 'variant_id'],
    item_code: ['item code (do not edit)', 'item code', 'code', 'sku'],
    barcode: ['barcode (do not edit)', 'barcode', 'bar code', 'ean'],
    item_name: ['item name', 'name', 'product name'],
    variant_name: ['variant name', 'variant', 'attribute'],
    category: ['category', 'category name'],
    unit_of_measurement: ['uom', 'unit', 'unit of measurement'],
    sale_price: ['sale price', 'price', 'retail price'],
    cost_price: ['cost price', 'cost'],
    wholesale_price: ['wholesale price', 'wholesale'],
    reseller_price: ['reseller price', 'reseller'],
    minimum_price: ['minimum price', 'min price'],
    maintain_stock: ['maintain stock', 'stock'],
    is_weighted: ['is weighted', 'weighted', 'sell by weight'],
    plu_code: ['plu code', 'plu'],
    item_tax_template: ['item tax template', 'tax template', 'tax'],
    ...cfAliases,
  },
  localStorageKey: 'data-import:product_update',
  onSuccessInvalidateKeys: [['items'], ['bulkItemSelection']],
  skipReasonLabels: {
    invalid_required: 'Item ID, Variant ID, Barcode, or Item Code required',
    manual_skip: 'Manually skipped',
  },
  normalizeRow: (raw) => {
    const base: Record<string, string> = {
      item_id: (raw.item_id || '').trim(),
      variant_id: (raw.variant_id || '').trim(),
      barcode: (raw.barcode || '').trim(),
      item_code: (raw.item_code || '').trim(),
      item_name: (raw.item_name || '').trim(),
      variant_name: (raw.variant_name || '').trim(),
      category: (raw.category || '').trim(),
      unit_of_measurement: (raw.unit_of_measurement || raw.uom || '').trim(),
      sale_price: (raw.sale_price || '').trim(),
      cost_price: (raw.cost_price || '').trim(),
      wholesale_price: (raw.wholesale_price || '').trim(),
      reseller_price: (raw.reseller_price || '').trim(),
      minimum_price: (raw.minimum_price || '').trim(),
      maintain_stock: parseFlag(raw.maintain_stock, false),
      is_weighted: parseFlag(raw.is_weighted, false),
      plu_code: (raw.plu_code || '').trim(),
      item_tax_template: (raw.item_tax_template || '').trim(),
    };
    customFields.forEach((cf) => {
      base[cf.fieldname] = (raw[cf.fieldname] || '').trim();
    });
    return base;
  },
  clientValidateRow: (row) => {
    if (!row.item_id?.trim() && !row.variant_id?.trim() && !row.barcode?.trim() && !row.item_code?.trim()) {
      return { reason: 'invalid_required', label: 'Item ID, Variant ID, Barcode, or Item Code is required' };
    }
    return null;
  },
  api: API,
  };
}
